import {
  ChainType as ChainTypeConst,
  SmartAccountType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import EnumRiskLevel from "@wallet-manager/node-types/dist/types/postgres/const/RiskLevel";
import EnumWalletType from "@wallet-manager/node-types/dist/types/postgres/const/WalletType";
import BigNumber from "bignumber.js";
import { groupBy, isEmpty } from "lodash";
import isNil from "lodash/isNil";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "react-admin";
import { batchSweep, batchSweepPreview } from "src/api/merchant";
import { AssetSingleSelection } from "src/components/AssetSelection";
import { ChainSingleSelection } from "src/components/ChainSelection";
import WalletVersionSingleSelection from "src/components/Common/WalletVersionSelection/WalletVersionSingleSelection";
import { useDatePicker } from "src/components/DatePicker";
import { useAssets, useChains } from "src/components/FetchConfig";
import {
  MultiSelection,
  NodeTypesEnumSingleSelection,
  SingleSelection,
} from "src/components/GeneralSelection";
import MpTextField from "src/components/MpTextField";
import { Box, Button, Checkbox, Container } from "src/components/MuiGenerals";
import VersionTabs, { VersionTabsArr } from "src/components/VersionTabs";
// import { InvokerAddressObj } from "src/features/common/types/InvokerAddressObj";
import { useAlerting, usePermission, useTranslation } from "src/hooks";
import { useAppSelector } from "src/reducer/hooks";
import { selectProfile } from "src/reducer/profileSlice";
import {
  enumChainIdToType,
  EnumNoRiskWallet,
  FeatureCodes,
} from "src/utils/constant";
import {
  amountDivideDecimals,
  bigNumStrMulitpleDecimals,
  containsOnlyNumbers,
  displayAmount,
  onlyAllowInteger,
  removeCommasFromNumStr,
} from "src/utils/helper";
import { DivideLine, genField } from "src/utils/HelperComp";
import { customSx } from "src/utils/styling";
import { useZusDialogStore } from "src/zustand/store";
import styles from "./batchSweep.module.scss";
import BatchSweepList from "./BatchSweepList";
import AddressSelectionColumn from "./components/AddressSelectionColumn";
import { getEnumRiskByMerchantLevel } from "./helpers/getEnumRiskByMerchantLevel";
import { getParams } from "./helpers/getParams";
import { specificSweepAmountBox, summaryBox } from "./styles/summary";
import { sx } from "./styles/sx";
import { FieldsFace, initFields } from "./types/FieldsFace";
import { FilterBarPropsType } from "./types/FilterBarPropsType";
import { PreviewPropsType } from "./types/PreviewPropsTypes";
import {
  initSweepingParamsRef,
  SweepingParamsRef,
} from "./types/sweepingParamsRefType";

const Pkey = FeatureCodes.assetManagement.BatchSweep;

export default function BatchSweep() {
  const [list, setList] = useState<any[]>([]);
  const [fields, setFields] = useState<FieldsFace>(initFields);
  const WalletSettlementTime = useDatePicker();
  const [previewParams, setPreviewParams] = useState({
    chain_name: "",
    currency: "",
    decimals: 1,
  });
  useEffect(() => {
    setFields((fields: any) => ({ ...fields, asset_name: "" }));
  }, [fields.chain_name]);

  //handle walletMajorVersion and versionTabs
  const [walletMajorVersion, setWalletMajorVersion] = useState(0);
  const [tabsArr, setTabsArr] = useState<VersionTabsArr[]>([]);

  const { selectAssetsByChain } = useAssets();
  const { chainObj, selectChainByPieces } = useChains();
  const profile = useAppSelector(selectProfile);
  const params = getParams(fields, selectChainByPieces, profile, {
    wallet_settlement_date_from: WalletSettlementTime.start || undefined,
    wallet_settlement_date_to: WalletSettlementTime.end || undefined,
  });
  const isSecondLayerToken = ((): boolean => {
    if (!(fields.chain_name && fields.asset_name)) {
      return false;
    }
    const { chain_id, chain_type } = selectChainByPieces({
      chain_name: fields.chain_name,
    });
    const isSecond = Object.values(chainObj).some(
      (obj) =>
        obj.chain_type === chain_type &&
        obj.chain_id === chain_id &&
        obj.native_asset_name !== fields.asset_name,
    );
    return isSecond;
  })();

  const sweepingParamsRef = useRef<SweepingParamsRef>(initSweepingParamsRef);
  const assets = selectAssetsByChain({ chain_name: fields.chain_name });

  const selectedAsset = assets.find(
    (item) => item.asset_name === fields.asset_name,
  );
  const rawReserveBalance = selectedAsset?.min_reserve_balance || "0";
  //decimals is supposed to be string, it's '-' when no currency found
  const reserveBalance =
    amountDivideDecimals(rawReserveBalance, Number(params.decimals)) || "0";
  const selectedAssetName = selectedAsset?.asset_name || "";
  const onNewList = (rawList: any[]) => {
    if (!rawList) return;
    const chain_name = chainObj[fields.chain_name].name;
    const currency = params.asset_name;
    setPreviewParams({
      chain_name,
      decimals: Number(params.decimals),
      currency,
    });
    const list = rawList.map((item) => {
      const diff = BigNumber(item.amount).minus(BigNumber(rawReserveBalance));
      const sweepableAmount = BigNumber.max(diff, 0);
      return { ...item, sweepableAmount };
    });

    setList(list);
    const { include_no_risk_level_wallet, ...rest } = params;
    sweepingParamsRef.current = rest;

    //handle version Tabs and wallet major version
    const groupedResult = groupBy(list, "wallet_major_version");
    const tabs: VersionTabsArr[] = Object.entries(groupedResult).map(
      (item) => ({
        walletMajorVersion: Number(item[0]),
        walletCounts: item[1]?.length,
      }),
    );
    const sortedTabs = tabs.sort(
      (itemA, itemB) => itemB.walletMajorVersion - itemA.walletMajorVersion,
    );
    setTabsArr(sortedTabs);
    const localWalletMajorVersion =
      sortedTabs.length > 0 ? sortedTabs[0].walletMajorVersion : 0;
    setWalletMajorVersion(localWalletMajorVersion);
  };

  const onSweep = (
    sweep_records?: {
      from_address: string;
      amount: string;
      decimals: number;
    }[],
  ) => {
    const getInvokerAddress = () => {
      if (!isSecondLayerToken) return "";
      return (
        fields.invokerAddressDictionary[walletMajorVersion]?.invokerAddress ??
        ""
      );
    };
    const getherAddress =
      fields.receivingAddressDictionary[walletMajorVersion]?.receivingAddress ??
      "";
    const params = {
      ...sweepingParamsRef.current,
      invoker_address: getInvokerAddress(),
      gather_address: getherAddress,
    };
    return batchSweep({ ...params, sweep_records });
  };
  const onReset = () => {
    setFields(initFields);
    WalletSettlementTime.resetDate();
    setList([]);
  };
  return (
    <Box sx={customSx.layoutBox}>
      <Container disableGutters maxWidth={false}>
        <FilterBar
          fields={fields}
          setFields={setFields}
          WalletSettlementTime={WalletSettlementTime}
          onNewList={onNewList}
          reserveBalance={Number(reserveBalance)}
          selectedAssetName={selectedAssetName}
        />
      </Container>
      {list.length > 0 && (
        <>
          <DivideLine />
          <Container
            style={customSx.datagridContainer}
            maxWidth={false}
            disableGutters>
            <Preview
              list={list}
              sweepFn={onSweep}
              reset={onReset}
              chain_name={previewParams.chain_name}
              fields={fields}
              setFields={setFields}
              currency={previewParams.currency}
              isSecondLayerToken={isSecondLayerToken}
              smartAccountType={sweepingParamsRef.current.smart_account_type}
              walletMajorVersion={walletMajorVersion}
              setWalletMajorVersion={setWalletMajorVersion}
              tabsArr={tabsArr}
            />
          </Container>
        </>
      )}
    </Box>
  );
}
const mustFill = ["chain_name", "asset_name", "threshold"];

function FilterBar(props: FilterBarPropsType) {
  const {
    onNewList,
    fields,
    setFields,
    reserveBalance,
    selectedAssetName,
    WalletSettlementTime,
  } = props;
  const hasReverve = reserveBalance !== 0;
  const translate = useTranslate();
  const t = (key: string, params = {}) =>
    translate(`assetManagement.${key}`, params);
  const profile = useAppSelector(selectProfile);
  const enumRisks = getEnumRiskByMerchantLevel(profile.acceptance_level);
  const { selectAssetNamesByChain, selectAssetsByChain } = useAssets();
  const { selectChainByPieces } = useChains();
  const { alerting } = useAlerting();

  const autoSweepAssetRiskLevelOptions =
    profile.autoSweepExcludedRiskLevels?.reduce(
      (acc: Record<string, string>, level) => {
        return { ...acc, [EnumRiskLevel[level]]: String(level) };
      },
      {},
    );

  const getIsAutoSweepAsset = (assetName: string) =>
    selectAssetsByChain({
      chain_name: fields.chain_name,
    })?.find(({ asset_name }) => asset_name === assetName)
      ?.merchant_asset_configs?.[0]?.enable_auto_sweep;

  const isAutoSweepAsset = getIsAutoSweepAsset(fields.asset_name);

  const setAssets = (asset_name: string) => {
    const isAutoSweepAsset = getIsAutoSweepAsset(asset_name);
    setFields((fields: any) => ({
      ...fields,
      asset_name,
      risk_level: isAutoSweepAsset === false ? "acceptant" : "",
      acceptant_risk_level_array: [],
    }));
  };
  const checkErrorFields = (): Object[] =>
    mustFill.filter((key) => isNil(fields[key]));
  useEffect(() => {
    if (fields.risk_level !== "acceptant")
      setFields((f: any) => ({ ...f, acceptant_risk_level_array: [] }));
  }, [fields.risk_level]);
  useEffect(() => {
    const showDecimal = fields.chain_name && fields.asset_name;
    if (!showDecimal) return;
    if (decimals === "-") alerting("warning", t("assetNotSupported"));
  }, [fields.chain_name, fields.asset_name]);
  const zusDialog = useZusDialogStore();
  const params = getParams(fields, selectChainByPieces, profile, {
    wallet_settlement_date_from: WalletSettlementTime.start || undefined,
    wallet_settlement_date_to: WalletSettlementTime.end || undefined,
  });
  const { decimals } = params;
  const asset_names = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });
  const onPreview = async () => {
    onNewList([]);
    if (Number(fields.max_sweepable_address) < 1) {
      return alerting("warning", t("minLessThanOne"));
    }
    if (params.smart_account_type === SmartAccountType.Kernel) {
      // only ETH smart account has 100 limit
      if (Number(fields.max_sweepable_address) > 100) {
        return alerting("warning", t("maxLargerThanOneHundred"));
      }
    } else {
      if (Number(fields.max_sweepable_address) > 1000) {
        return alerting("warning", t("maxLargerThanOneThousand"));
      }
    }
    const errorFields = checkErrorFields();
    if (errorFields.length > 0) {
      const message = `Missing fields ${errorFields.join(", ")}`;
      return alerting("warning", message);
    }
    if (Number(fields.threshold) <= reserveBalance) {
      const tParams = { asset: selectedAssetName, amount: reserveBalance };
      return alerting("warning", t("preview_warning", tParams));
    }
    if (
      profile.chain_analysis &&
      fields.risk_level === "acceptant" &&
      fields.acceptant_risk_level_array.length === 0
    ) {
      return alerting(
        "warning",
        t("ph_batch_sweep_acceptant_risk_level_warning"),
      );
    }
    if (profile.chain_analysis && isAutoSweepAsset && !fields.risk_level) {
      return alerting("warning", t("ph_batch_sweep_risk_level"));
    }
    zusDialog.openExtra("loadingDialog");
    const { invokerAddressDictionary, ...previewParams } = params;
    const { sweep_records } = (await batchSweepPreview(previewParams)) ?? {};
    zusDialog.closeExtra();

    if (sweep_records && !sweep_records.length) {
      return alerting("warning", t("no_data_to_preview"));
    }
    onNewList(sweep_records);
  };

  const onChange =
    (type: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim();
      const name = e.target.name;
      if (name === "max_sweepable_address") {
        const isInteger = onlyAllowInteger(value);
        if (!isInteger) {
          return alerting("warning", t("allowIntOnly"));
        }
      }
      const resp = name === "threshold" ? containsOnlyNumbers(value) : true;
      if (!resp) {
        return;
      }

      setFields((fields: any) => ({ ...fields, [type]: value }));
    };
  const canPreview = fields.asset_name.length > 0 && fields.threshold !== "";
  const warningMessage = [
    t("minimum_reserve_requirement"),
    reserveBalance,
    selectedAssetName,
  ].join(" ");

  const isXrp = fields.asset_name === "XRP" && fields.chain_name === "XRPT";

  const isTron = enumChainIdToType.TRON === String(params.chain_type);

  const F = genField({ t }, [
    [
      "chain_name",
      <ChainSingleSelection
        label={t("phChain_name")}
        setChoice={(chain_name) =>
          setFields((f: any) => ({
            ...f,
            chain_name,
            is_smart_account: false,
            is_free_bandwidth_only: false,
            max_sweepable_address: "1000",
            risk_level: "",
            acceptant_risk_level_array: [],
          }))
        }
        choice={fields.chain_name}
      />,
    ],
    [
      "asset_name",
      <AssetSingleSelection
        label={t("phAsset_name")}
        setChoice={setAssets}
        choice={fields.asset_name}
        allItems={asset_names}
      />,
    ],
    [
      "threshold",
      <MpTextField
        name={"threshold"}
        label={t("phThreshold")}
        type="string"
        value={fields.threshold}
        onChange={onChange("threshold")}
        helperText={hasReverve && isXrp && warningMessage}
      />,
    ],
    [
      "max_sweepable_address",
      <MpTextField
        name={"max_sweepable_address"}
        label={t("ph_max_sweepable_address")}
        value={fields.max_sweepable_address}
        onChange={onChange("max_sweepable_address")}
      />,
    ],
    [
      "risk_level",
      <SingleSelection
        label={t("ph_batch_sweep_risk_level")}
        value={fields.risk_level}
        onChange={(e) =>
          setFields((f: any) => ({ ...f, risk_level: e.target.value }))
        }
        enumData={
          isAutoSweepAsset && autoSweepAssetRiskLevelOptions
            ? autoSweepAssetRiskLevelOptions
            : enumRisks.risk
        }
        disabled={!profile.chain_analysis || isAutoSweepAsset === undefined}
        isNoSorting
        clearSelect={() =>
          setFields((f: any) => ({
            ...f,
            risk_level: isAutoSweepAsset === false ? "acceptant" : "",
          }))
        }
      />,
    ],
    [
      "acceptant_risk_level",
      <MultiSelection
        label={t("ph_batch_sweep_acceptant_risk_level")}
        value={fields.acceptant_risk_level_array}
        onChange={(e) => {
          const newArray = e.target.value;
          setFields((f: any) => ({
            ...f,
            acceptant_risk_level_array: newArray,
          }));
        }}
        disabled={fields.risk_level !== "acceptant" || !profile.chain_analysis}
        enumData={enumRisks.acceptant}
        isNoSorting
      />,
    ],
    [
      "include_no_risk_level_wallet_filter",
      <NodeTypesEnumSingleSelection
        label={""}
        value={fields.include_no_risk_level_wallet}
        onChange={(e) =>
          setFields((f: any) => ({
            ...f,
            include_no_risk_level_wallet: e.target.value,
          }))
        }
        enumData={EnumNoRiskWallet}
        isNoSorting
        clearSelect={() => {}}
      />,
    ],
    [
      "wallet_settlement_time",
      <WalletSettlementTime.Picker type={"dateTime"} />,
    ],
    [
      isTron ? "auto_resource_rent" : "smart_account",
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          height: "100%",
          paddingTop: 12,
        }}>
        <div style={{ display: "flex", gap: "40px", lineHeight: 1 }}>
          <div>
            <Checkbox
              checked={fields.is_smart_account}
              onChange={(e) =>
                setFields((f: any) => {
                  const getMaxSweepableAddress = () => {
                    if (
                      e.target.checked &&
                      enumChainIdToType.ETH === String(params.chain_type)
                    ) {
                      return "100";
                    }

                    return "1000";
                  };

                  return {
                    ...f,
                    is_smart_account: e.target.checked,
                    is_free_bandwidth_only: e.target.checked && isTron,
                    max_sweepable_address: getMaxSweepableAddress(),
                  };
                })
              }
            />
          </div>
          {isTron && (
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}>
                <span style={{ fontSize: "11px", fontWeight: 500 }}>
                  {t("free_bandwidth_only")}
                </span>
                <Checkbox
                  disabled={!fields.is_smart_account}
                  checked={fields.is_free_bandwidth_only}
                  onChange={(e) =>
                    setFields((f: any) => {
                      return {
                        ...f,
                        is_free_bandwidth_only: e.target.checked,
                      };
                    })
                  }
                />
              </div>
              <div
                style={{
                  overflow: "visible",
                  fontSize: "9px",
                  lineHeight: 1.1,
                  marginTop: -10,
                }}>
                {t("free_bandwidth_only_remarks")}
              </div>
            </div>
          )}
        </div>
      </div>,
    ],
    [
      "version",
      <WalletVersionSingleSelection
        value={fields.wallet_version}
        onChange={(wallet_version) =>
          setFields((f: any) => ({ ...f, wallet_version }))
        }
        chainType={params.chain_type}
        walletTypes={[EnumWalletType.ClientWallet]}
      />,
    ],
  ]);

  return (
    <Container disableGutters maxWidth={false}>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.chain_name}
        {F.asset_name}
        {F.address}
        {F.threshold}
        {F.risk_level}
        {F.acceptant_risk_level}
        {F.max_sweepable_address}
        {F.include_no_risk_level_wallet_filter}
        {F.wallet_settlement_time}
        {params.smart_account_type !== undefined && !isTron && F.smart_account}
        {params.smart_account_type !== undefined &&
          isTron &&
          F.auto_resource_rent}
        {F.version}
      </Box>

      <Box sx={customSx.filterB}>
        <Button
          disabled={!canPreview}
          onClick={onPreview}
          sx={sx.preview}
          color="secondary"
          variant="contained">
          {t("preview")}
        </Button>
      </Box>
    </Container>
  );
}

function Preview(props: PreviewPropsType) {
  const {
    list = [],
    reset = () => {},
    sweepFn = () => {},
    chain_name = "",
    fields,
    setFields,
    currency = "",
    isSecondLayerToken,
    smartAccountType,
    walletMajorVersion,
    setWalletMajorVersion,
    tabsArr,
  } = props;
  const { t } = useTranslation("assetManagement");
  const { hasPermission } = usePermission();
  const { alerting } = useAlerting();

  const inputFieldRef = useRef<HTMLInputElement>(null);
  const previousInputFieldValue = useRef<string>("");
  const inputDivRef = useRef<HTMLDivElement>(null);
  const [specificSweepAmount, setSpecificSweepAmount] = useState("");
  const [addressArr, setAddressArr] = useState<
    { fromAddress: string; amountToSweep: string; isChecked: boolean }[]
  >([]);

  const versionedList = useMemo(() => {
    const filtedList = list.filter(
      (item) => item.wallet_major_version === walletMajorVersion,
    );
    return filtedList.length > 0 ? filtedList : [];
  }, [list, walletMajorVersion]);

  const { chainObj } = useChains();
  const utxoChainTypeArr = [
    ChainTypeConst.BTC,
    ChainTypeConst.BCH,
    ChainTypeConst.LTC,
    ChainTypeConst.ADA,
    ChainTypeConst.DOGE,
  ];
  const { chain_type, type: chain_shortname } = Object.entries(chainObj)
    .filter(([_type, chainInfo]) => chainInfo?.name === chain_name)
    .map(([_type, chainInfo]) => chainInfo)[0];
  const isUTXOType = utxoChainTypeArr.includes(chain_type);
  const [newList, setNewList] = useState<any[]>([]);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    setAddressArr(
      versionedList.map(({ from_address: fromAddress, sweepableAmount }) => ({
        fromAddress,
        amountToSweep: amountDivideDecimals(sweepableAmount, decimals),
        isChecked: true,
      })),
    );
    setSpecificSweepAmount("");
    inputFieldRef.current!.value = "";
    setNewList(() => {
      return (versionedList || []).map((item: any, index: number) => {
        const { amount, path, wallet_address, sweepableAmount, ...rest } = item;
        return {
          ...rest,
          originalAmount: sweepableAmount,
          amount: displayAmount(sweepableAmount, item.decimals),
          id: index + 1,
        };
      });
    });
  }, [list, walletMajorVersion]);
  const total = list.length;
  let sum = BigNumber(0);
  for (let { sweepableAmount } of versionedList) {
    sum = sum.plus(sweepableAmount);
  }

  const decimals = versionedList[0]?.decimals ? versionedList[0]?.decimals : 0;
  const sumString = displayAmount(sum.toFixed(), decimals);

  const totalAmountToSweep = displayAmount(
    addressArr
      .reduce((acc, { amountToSweep }) => {
        if (!Number(amountToSweep)) {
          return acc;
        }
        return acc.plus(
          BigNumber(bigNumStrMulitpleDecimals(amountToSweep, decimals)),
        );
      }, BigNumber(0))
      .toFixed(),
    decimals,
  );

  const onCoinSweep = async () => {
    const isSmartAccount =
      smartAccountType === SmartAccountType.Kernel ||
      smartAccountType === SmartAccountType.TronRentalEnergyBandwidth;

    if (addressArr.every(({ isChecked }) => !isChecked)) {
      return alerting("error", t("no_address_warning"));
    }
    const zeroOrEmpty = addressArr.find(
      ({ amountToSweep, isChecked }) =>
        (amountToSweep === "0" || !BigNumber(amountToSweep)) && isChecked,
    );
    if (zeroOrEmpty) {
      return alerting("error", t("bigger_than_0_warning"));
    }
    if (
      isSecondLayerToken &&
      !fields.invokerAddressDictionary[walletMajorVersion]?.invokerAddress &&
      !isSmartAccount
    ) {
      return alerting("warning", t("no_invoker_address_warning"));
    }
    if (
      !fields.receivingAddressDictionary[walletMajorVersion]?.receivingAddress
    ) {
      return alerting("warning", t("no_receiving_address_warning"));
    }
    const sweepRecordArr = addressArr
      .filter(({ isChecked }) => isChecked)
      .map(({ amountToSweep, fromAddress: from_address }) => ({
        amount: bigNumStrMulitpleDecimals(amountToSweep, decimals),
        from_address,
        decimals,
      }));
    const res = await sweepFn(sweepRecordArr);
    if (!res) return;
    const message = `${
      t("cryptSweeped") + ", " + t("batch_id") + ": " + res.batch_id
    }`;
    alerting("success", message);
  };

  const autoCalBtnHandler = () => {
    if (!Number(specificSweepAmount)) {
      return alerting("error", t("bigger_than_0_warning"));
    }
    let toSweepAmount = BigNumber(
      bigNumStrMulitpleDecimals(specificSweepAmount, decimals),
    );
    if (toSweepAmount.isGreaterThan(sum)) {
      return alerting("error", t("excessive_amt_warning"));
    }

    const toSweepAmountArr: {
      fromAddress: any;
      amountToSweep: string;
      isChecked: boolean;
    }[] = [];
    for (let i = 0; i < versionedList.length; i++) {
      const { sweepableAmount, from_address: fromAddress } = versionedList[i];
      if (toSweepAmount.isEqualTo(BigNumber(0))) {
        toSweepAmountArr.push({
          fromAddress,
          amountToSweep: "0",
          isChecked: false,
        });
      } else if (
        BigNumber(sweepableAmount).isGreaterThanOrEqualTo(toSweepAmount)
      ) {
        toSweepAmountArr.push({
          fromAddress,
          amountToSweep: amountDivideDecimals(
            toSweepAmount.toFixed(),
            decimals,
          ),
          isChecked: true,
        });
        toSweepAmount = BigNumber(0);
      } else {
        toSweepAmount = toSweepAmount.minus(BigNumber(sweepableAmount));
        toSweepAmountArr.push({
          fromAddress,
          amountToSweep: amountDivideDecimals(sweepableAmount, decimals),
          isChecked: true,
        });
      }
      if (i === versionedList.length - 1) {
        setAddressArr(toSweepAmountArr);
        const checkboxElementArr = tableBodyRef.current
          ? (tableBodyRef.current.querySelectorAll(
              "input[type='checkbox']",
            ) as NodeListOf<HTMLInputElement>)
          : [];
        const inputFieldElementArr = tableBodyRef.current
          ? (tableBodyRef.current.querySelectorAll(
              "input[name='amountToSweep']",
            ) as NodeListOf<HTMLInputElement>)
          : [];
        checkboxElementArr.forEach(
          (ele, i) => (ele.checked = toSweepAmountArr[i].isChecked),
        );
        inputFieldElementArr.forEach((ele, i) => {
          ele.value = toSweepAmountArr[i].amountToSweep;
          if (!Number(toSweepAmountArr[i].amountToSweep)) {
            ele.setAttribute("disabled", "true");
          } else {
            ele.removeAttribute("disabled");
          }
        });
      }
    }
  };
  //items in summary
  const F = genField({ t }, [
    [
      "chain_name_and_currency",
      <div>
        {chain_name} / {currency}
      </div>,
    ],
    ["max_address_no", <div>{versionedList.length}</div>],
    ["max_sweepable_amount", <div>{sumString}</div>],
    ["decimals", <div>{decimals}</div>],
    [
      "address_selected",
      <div>{addressArr.filter(({ isChecked }) => isChecked).length}</div>,
    ],
    ["amount_to_sweep", <div>{totalAmountToSweep}</div>],
    [
      "specific_sweep_amount",
      <div style={{ display: "flex", height: "100%" }}>
        <MpTextField
          inputRef={inputFieldRef}
          innerRef={inputDivRef}
          disabled={isUTXOType}
          onChange={(e) => {
            const valueWithoutCommas = removeCommasFromNumStr(e.target.value);

            if (!containsOnlyNumbers(valueWithoutCommas)) {
              inputFieldRef.current!.value = previousInputFieldValue.current;
              return;
            }
            const inputDecimals = e.target.value.split(".")[1]
              ? e.target.value.split(".")[1].length
              : 0;
            if (inputDecimals > decimals) {
              inputFieldRef.current!.value = inputFieldRef.current!.value.slice(
                0,
                -1,
              );
              return;
            }
            inputFieldRef.current!.value = valueWithoutCommas;
            previousInputFieldValue.current = valueWithoutCommas;
          }}
          onBlur={() => setSpecificSweepAmount(inputFieldRef.current!.value)}
          placeholder={t("ph_specific_sweep_amount")}
          sx={{
            height: "100% !important",
            div: { height: "100% !important" },
            input: { paddingTop: "12px", height: "100%" },
          }}
        />
        <Button
          disabled={isUTXOType}
          color="secondary"
          variant="contained"
          style={{ height: "32px", marginTop: "6px", marginLeft: "12px" }}
          onClick={autoCalBtnHandler}>
          {t("auto_cal")}
        </Button>
      </div>,
    ],
  ]);
  if (total === 0) return <></>;
  return (
    //summary
    <Container
      style={customSx.datagridContainer}
      maxWidth={false}
      disableGutters>
      <VersionTabs
        walletMajorVersion={walletMajorVersion}
        tabsArr={tabsArr}
        setWalletMajorVersion={(walletMajorVersion) => {
          setWalletMajorVersion(walletMajorVersion);
        }}
      />
      <Box sx={summaryBox} className={styles.previewSummary}>
        {F.chain_name_and_currency}
        {F.max_address_no}
        {F.max_sweepable_amount}
        {F.decimals}
        {F.address_selected}
        {F.amount_to_sweep}
      </Box>
      <br />
      <Box sx={specificSweepAmountBox} className="gridFilter">
        {F.specific_sweep_amount}
      </Box>
      <br />
      <BatchSweepList
        list={newList}
        addressArr={addressArr}
        setAddressArr={setAddressArr}
        totalAmountToSweep={totalAmountToSweep}
        sumString={sumString}
        isUTXOType={isUTXOType}
        tableBodyRef={tableBodyRef}
      />
      <AddressSelectionColumn
        chain_name={chain_shortname}
        smartAccountType={smartAccountType}
        isSecondLayerToken={isSecondLayerToken}
        fields={fields}
        setFields={setFields}
        walletMajorVersion={walletMajorVersion}
      />
      <div style={sx.previewActions}>
        {hasPermission(Pkey.CoinSweep) && (
          <Button onClick={onCoinSweep} color="secondary" variant="contained">
            {t("coinSweep")}
          </Button>
        )}
        <Button color="info" onClick={reset} variant="contained">
          {t("reset")}
        </Button>
      </div>
    </Container>
  );
}
